import React, { useEffect, useMemo, useState } from 'react';
import { Guide } from '@/pages/home/components/Guide';
import { getLocale, useDispatch, useLocation, useSelector, history, useIntl } from 'umi';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import guideImg_2_2_cn from '@/pages/dashboard/assets/images/guide/cn/guide-dbd-feature2-2.png';
import guideImg_2_2_en from '@/pages/dashboard/assets/images/guide/en/guide-dbd-feature2-2.png';
import guideImg_2_3_cn from '@/pages/dashboard/assets/images/guide/cn/guide-dbd-feature2-2.png';
import guideImg_2_3_en from '@/pages/dashboard/assets/images/guide/en/guide-dbd-feature2-2.png';

import './index.scss';
import {
  DASHBOARD_GUIDE_BOX_LIST2,
  DASHBOARD_GUIDE_HIDE,
  DASHBOARD_SHOW_GUIDE_GAME_LIST,
} from '@/pages/home/components/GuideModule/GuideDashboardBox';
import { storage } from '@/utils/utils';
import { GUIDE_TYPE_DASHBOARD_REALTIME, GUIDE_SYS_DASHBOARD } from '@/pages/home/models/guide';

const STEP1_PATH = '/dashboard/game/demo/realtime/users';
const STEP2_PATH = '/dashboard/game/demo/realtime/users/byCountryRegionZone';
const STEP3_PATH = '/dashboard/game/demo/realtime/users/changeAnalysis';
const STEP_PATH_LIST = [STEP1_PATH, STEP2_PATH, STEP3_PATH];

const GuideRealTimeUser = (props: any) => {
  const [currentGuideStep, setCurrentGuideStep] = useState(-3);
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEn = locale === 'en-US';
  const dispatch = useDispatch();
  const location = useLocation();
  const { guideType, userInfo } = useSelector((state: any) => {
    const { guide: { guideType }, home: { userInfo } } = state;
    return {
      guideType,
      userInfo
    }
  });
  const guideUsersHide = storage.getLocalStorageItem(DASHBOARD_GUIDE_HIDE);
  // 注意下面两个useEffect不要合并到一起，不然会出现：从头部导航进入新手引导，点击到下一步时，关闭按钮无法关闭，而是返回到第一步
  useEffect(() => {
    if (guideType === GUIDE_TYPE_DASHBOARD_REALTIME) {
      const { pathname } = location;
      if (pathname !== STEP1_PATH) {
        history.push(STEP1_PATH);
      }
      setCurrentGuideStep(0);
    }
  }, [guideType]);

  useEffect(() => {
    const list = storage.getLocalStorageItem(DASHBOARD_GUIDE_BOX_LIST2) || [];
    const guideHide = storage.getLocalStorageItem(DASHBOARD_GUIDE_HIDE);
    if (userInfo?.openid && !list.includes(userInfo.openid) && !guideHide) {
      const { pathname } = location;
      if (pathname !== STEP1_PATH  && !guideUsersHide) {
        history.push(STEP1_PATH);
      }
      setCurrentGuideStep(0);
    }
  }, [userInfo])

  // 流程完成后保存至local storage
  const updateGuideCompletionStatus = () => {
    if (userInfo.openid) {
      const list = storage.getLocalStorageItem(DASHBOARD_GUIDE_BOX_LIST2) || [];
      !list?.includes(userInfo.openid) && list.push(userInfo.openid);
      storage.setLocalStorageItem(DASHBOARD_GUIDE_BOX_LIST2, list);
    }
  };

  const onFinished = () => {
    updateGuideCompletionStatus();

    dispatch({
      type: 'guide/showDashboardGuideModule',
      payload: true,
    });
  }

  const onCloseStep = () => {
    updateGuideCompletionStatus();

    dispatch({
      type: 'guide/save',
      payload: {
        guideSys: GUIDE_SYS_DASHBOARD,
        guideType: '',
      },
    });
  }

  const onStepChange = (current: number) => {
    const { pathname } = location;
    if (current >= 0 && pathname !== STEP_PATH_LIST[current]) {
      history.push(STEP_PATH_LIST[current]);
    }
    setCurrentGuideStep(current);
  }

  const steps = useMemo(() => {
    return [
      {
        highlightElements: [{
          element: document.getElementsByClassName('menu-realtime-user')?.[0]
        }, {
          element: () => document.querySelectorAll('.realtime-users-tab .tea-tabs__tab')?.[0],
          padding: { paddingLeft: 4, paddingRight: 4 },
          hasRadius: true
        }],
        element: () =>
          document.getElementsByClassName(
            'js-realtime-datepicker',
          )?.[0],
        title: formatMessage({ id: 'guide_realtime_user_step1_title' }),
        subtitle: formatMessage({ id: 'guide_realtime_user_step1_subtitle' }),
        description: formatMessage({ id: 'guide_realtime_user_step1_description' }),
        placement: 'bottom-center',
      },
      {
        highlightElements: [{
          element: document.getElementsByClassName('menu-realtime-user')?.[0]
        }, {
          element: () => document.querySelectorAll('.realtime-users-tab .tea-tabs__tab')?.[1],
          padding: { paddingLeft: 4, paddingRight: 4 },
          hasRadius: true
        }],
        element: () =>
          document.getElementsByClassName(
            'js-realtime-dropdown-dimension',
          )?.[0],
        title: formatMessage({ id: 'guide_realtime_user_step2_title' }),
        subtitle: formatMessage({ id: 'guide_realtime_user_step2_subtitle' }),
        description: formatMessage({ id: 'guide_realtime_user_step2_description' }),
        placement: 'left-start',
        extImage: {
          image: isEn ? guideImg_2_2_en : guideImg_2_2_cn,
          placement: 'bottom-start',
          style: { width: '198px' },
        },
      },
      {
        highlightElements: [{
          element: document.getElementsByClassName('menu-realtime-user')?.[0]
        }, {
          element: () => document.querySelectorAll('.realtime-users-tab .tea-tabs__tab')?.[2],
          padding: { paddingLeft: 4, paddingRight: 4 },
          hasRadius: true
        }],
        element: () =>
          document.getElementsByClassName('margin-select-width')?.[0],
        title: formatMessage({ id: 'guide_realtime_user_step3_title' }),
        subtitle: formatMessage({ id: 'guide_realtime_user_step3_subtitle' }),
        description: formatMessage({ id: 'guide_realtime_user_step3_description' }),
        placement: 'right-start',
        extImage: {
          image: isEn ? guideImg_2_3_en : guideImg_2_3_cn,
          placement: 'bottom-start',
          style: { width: '198px' },
        },
      },
    ];
  }, [currentGuideStep, locale]);

  return (
    // finishText 根据中英文调整
    <Guide
      current={currentGuideStep}
      onCurrentChange={(
        current: number,
        context: { from: 'start' | 'back' | 'next' | 'finish' | 'cancel' },
      ) => {
        const { from } = context;
        onStepChange(current);
        if (from === 'finish') {
          onFinished();
        } else if (from === 'cancel') {
          onCloseStep();
        }
      }}
      visibleCloseBtn
      hideCancelButton
      showBackButton
      nextButtonTheme="primary"
      backText={formatMessage({ id: 'guide_last_step' })}
      nextText={formatMessage({ id: 'guide_next_step' })}
      finishText={formatMessage({ id: 'guide_more_step' })}
      steps={steps}
    />
  );
};

export default () => {
  const { currentGame } = useSelector(state => {
    // @ts-ignore
    const { currentGame } = state.dashboard;
    return {
      currentGame
    }
  });

  return <>
    {currentGame && DASHBOARD_SHOW_GUIDE_GAME_LIST.includes(currentGame.game_code) && <GuideRealTimeUser />}
  </>
}
